import { Button } from "@9amhealth/shared";
import type { FC } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useBloc } from "src/state/state";
import { FeatureFlags } from "src/lib/featureFlags";
import { PARTNER_CONFIGS } from "src/constants/partnerConfig";
import type { KnownPartnerSources } from "src/constants/partner";
import { APP_CONTENT_WIDTH_WITHOUT_PADDING } from "src/constants/layout";
import { Divider } from "src/ui/styled/X";
import Translate from "../Translate/Translate";
import { LoginView } from "./LoginForm";
import { getHeadingProps, useFocusHeading } from "src/lib/useFocusHeading";
import styled from "@emotion/styled";
import ContinueWithPartnerButton from "./ContinueWithPartnerButton";

const HeadingSection = styled.section`
  max-width: ${APP_CONTENT_WIDTH_WITHOUT_PADDING}px;
  margin: 0 auto;
  padding: 0 var(--space-md);
`;

const ContentSection = styled.div`
  padding: 0 var(--space-md);
`;

const PartnerButtonList = styled.div`
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
`;

const LoginOptions: FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { headingRef } = useFocusHeading();
  const [, { getFlag }] = useBloc(FeatureFlags);

  const enabledPartners = Object.entries(PARTNER_CONFIGS).filter(([, config]) =>
    getFlag(config.ssoLoginFeatureFlag)
  );

  const handleLoginButtonClick = () => {
    const searchParams = new URLSearchParams(location.search);
    searchParams.set("view", LoginView.loginForm);
    navigate({
      pathname: location.pathname,
      search: searchParams.toString()
    });
  };

  return (
    <>
      <HeadingSection>
        <h1 className="as-h4-large" ref={headingRef} {...getHeadingProps()}>
          <Translate msg={"login_title"} />
        </h1>
        <div style={{ height: "var(--space-sm)" }} />
        <p className="m0 color-c-80">
          <Translate msg={"login.subtitle"} variables={{ context: "manage" }} />
        </p>
      </HeadingSection>
      <div style={{ height: "var(--space-xl)" }} />
      <ContentSection>
        <div style={{ display: "flex", flexDirection: "column" }}>
          <Button theme="sunrise" onPress={() => handleLoginButtonClick()}>
            <Translate msg="signin" variables={{ context: "email" }} />
          </Button>

          {enabledPartners.length > 0 && (
            <>
              <div style={{ height: "var(--space-sm)" }} />
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  color: "var(--color-gray)"
                }}
              >
                <Divider style={{ flex: 1 }} />
                <span style={{ flex: "0.2", textAlign: "center" }}>
                  <Translate msg="or" />
                </span>
                <Divider style={{ flex: 1 }} />
              </div>
              <div style={{ height: "var(--space-sm)" }} />
              <PartnerButtonList>
                {enabledPartners.map(([source]) => (
                  <ContinueWithPartnerButton
                    key={source}
                    partner={source as KnownPartnerSources}
                  />
                ))}
              </PartnerButtonList>
            </>
          )}
        </div>
      </ContentSection>
    </>
  );
};

export default LoginOptions;
